/**
 * Shared wording for the call screens: phone numbers, outcomes, reasons,
 * and the backend's failure codes turned into something staff can act on.
 */
import type { ApiFailure } from "../../api/callsAPI";

/** "+17175550101" → "(717) 555-0101"; anything else is shown as given. */
export function formatPhone(value: string | null | undefined): string {
  if (!value) return "";
  const digits = value.replace(/\D/g, "");
  const national = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (national.length === 10) {
    return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
  }
  return value;
}

/** The last ten digits, so "+17175550101" and "717-555-0101" compare equal. */
export function phoneKey(value: string | null | undefined): string {
  return (value || "").replace(/\D/g, "").slice(-10);
}

export const OUTCOME_LABELS: Record<string, string> = {
  ai_resolved: "Handled by the AI",
  connected: "Answered by staff",
  voicemail: "Voicemail",
  callback_requested: "Callback requested",
  abandoned: "Caller hung up",
  message_played: "After-hours message",
  failed: "Failed",
};

export const REASON_LABELS: Record<string, string> = {
  billing: "Billing",
  enrollment: "Enrollment",
  scheduling: "Scheduling",
  account_change: "Account change",
  cancellation: "Cancellation",
  complaint: "Complaint",
  injury_or_safety: "Injury or safety",
  technical: "Technical problem",
  sales_inquiry: "New customer inquiry",
  other: "Other",
};

export const MESSAGE_KIND_LABELS: Record<string, string> = {
  voicemail: "Voicemail",
  callback_request: "Callback request",
  missed_call: "Missed call",
};

export function reasonLabel(reason: string | null | undefined): string {
  if (!reason) return "";
  return REASON_LABELS[reason] || reason.replace(/_/g, " ");
}

export function outcomeLabel(outcome: string | null | undefined, state?: string): string {
  if (outcome) return OUTCOME_LABELS[outcome] || outcome.replace(/_/g, " ");
  return state && state !== "completed" ? "In progress" : "—";
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return "—";
  const s = Math.max(0, Math.round(seconds));
  const m = Math.floor(s / 60);
  const rest = s % 60;
  return `${m}:${rest < 10 ? "0" : ""}${rest}`;
}

export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * The backend's failure, said so staff can fix it. `names` turns user and
 * group ids in the details back into names.
 */
export function describeCallsError(
  failure: ApiFailure | null | undefined,
  names: { users?: Record<string, string>; groups?: Record<string, string> } = {}
): string {
  if (!failure) return "";
  const users = (ids?: string[]) =>
    (ids || []).map((id) => names.users?.[id] || "a staff member").join(", ");
  const numbers = (list?: string[]) => (list || []).map(formatPhone).join(", ");
  switch (failure.code) {
    case "LOOP_NUMBER":
      return `${numbers(failure.numbers)} ${
        (failure.numbers || []).length === 1 ? "is" : "are"
      } your club's own line. Ringing it would send the caller straight back to the AI receptionist, so it can't be used here.`;
    case "INVALID_NUMBER":
      return failure.error || "That is not a US phone number.";
    case "MEMBER_NOT_ELIGIBLE":
      return `${users(failure.user_ids)} can't be added: their role does not have the "Answer calls" permission. Give the role that permission under Staff > Roles first.`;
    case "UNKNOWN_USER":
      return `${users(failure.user_ids)} is not a staff member of this club.`;
    case "UNKNOWN_GROUP":
      return "One of the chosen call groups no longer exists. Reload the page and choose again.";
    case "UNSUPPORTED_RING_STRATEGY":
      return "That ring style isn't available yet. Use \"Everyone at once\" or \"One at a time\".";
    case "GROUP_IN_USE":
      return "This group is still in the ring order or the after-hours settings. Remove it there first, then delete it.";
    case "CALLS_NOT_CONFIGURED":
      return "Calling isn't set up for Club Central yet. Contact Club Central support.";
    case "RATE_LIMITED":
      return "Too many requests. Wait a minute and try again.";
    case "INVALID_FIELD":
      return failure.error || "One of the values is not valid.";
    case "NETWORK_ERROR":
      return "Couldn't reach Club Central. Check your connection and try again.";
    default:
      return failure.error || "Something went wrong. Try again.";
  }
}
